let canvas = null;

const prepareCanvas = async function () {
    if (canvas) return;

    canvas = document.createElement('canvas');
    canvas.width = 100;
    canvas.height = 1;

    const gradient = document.getElementById('password-strength-gradient');
    const xml = new XMLSerializer().serializeToString(gradient);
    const image = document.createElement('img');

    await new Promise((resolve) => {
        image.addEventListener('load', () => {
            canvas.getContext('2d').drawImage(image, 0, 0);
            resolve();
        });

        image.src = 'data:image/svg+xml;base64,' + btoa(xml);
    });
};

const visibilityListener = function () {
    document.querySelectorAll('.password-input').forEach((element) => {
        if (document.hidden) {
            Helper.getAlpineData(element).hide = true;
        }
    });
};

// PasswordInput component
export default function () {
    let root;

    return {
        show: false,
        hide: true,

        init() {
            root = this.$root;
            const input = root.querySelector('input');
            const progressBar = root.querySelector('.progress-bar');

            this.$watch('show', (value) => {
                this.hide = !value;
                input.type = value ? 'text' : 'password';
            });

            this.$watch('hide', (value) => {
                this.show = !value;
            });

            document.addEventListener('visibilitychange', visibilityListener);

            if (!progressBar) return;

            const progressInfo = root.querySelector('.progress-info');

            (async () => {
                await prepareCanvas();

                input.addEventListener('input', () => {
                    const score = this.calculateScore(input.value);
                    const [red, green, blue] = canvas.getContext('2d').getImageData(score - 1, 0, 1, 1).data;

                    progressBar.style.backgroundColor = `rgb(${red}, ${green}, ${blue})`;
                    progressBar.style.width = `${score}%`;
                    progressInfo.textContent = this.getInfoText(score);
                });
            })();
        },

        destroy() {
            this.$nextTick(() => {
                if (!document.querySelector('.password-input')) {
                    canvas = null;
                    document.removeEventListener('visibilitychange', visibilityListener);
                }
            });
        },

        toggle() {
            this.show = !this.show;
        },

        calculateScore(value) {
            // Initial score
            let score = Math.min(value.length * 10, 120);

            // Bonus score if length > 12
            if (value.length > 12) {
                score += (value.length - 12) * 3;
            }

            // Deduct score if length < 8
            if (value.length < 8) {
                score -= (8 - value.length) * 3;
            }

            // Deduct score if no numbers are used
            if (!/\d/.test(value)) {
                score -= 11;
            }

            // Deduct score if no lowercase letters are used
            if (!/[a-z]/.test(value)) {
                score -= 13;
            }

            // Deduct score if no uppercase letters are used
            if (!/[A-Z]/.test(value)) {
                score -= 15;
            }

            // Deduct score if no symbols are used
            if (!/[^0-9a-z]/i.test(value)) {
                score -= 17;
            }

            // Deduct score if only letter, numbers and spaces are used
            if (/^[0-9a-z\s]+$/i.test(value)) {
                score -= Math.min(value.length * 2, 10);
            }

            // Deduct score if only letters are used
            if (/^[a-z]+$/i.test(value)) {
                score -= Math.min(value.length * 3, 15);
            }

            // Deduct score if only numbers are used
            if (/^\d+$/.test(value)) {
                score -= Math.min(value.length * 4, 20);
            }

            // Deduct score for repeated characters, consecutive sequences and reverse consecutive sequences
            const repeatedCharacters = new Set();
            const consecutiveSequences = new Set();
            const reverseConsecutiveSequences = new Set();
            let repeatedCount = 0;
            let consecutiveCount = 0;
            let reverseConsecutiveCount = 0;

            for (let i = 0; i < value.length - 1; i++) {
                const currentChar = value.codePointAt(i);
                const nextChar = value.codePointAt(i + 1);

                // Check for repeated characters (e.g., 'aaa')
                if (nextChar === currentChar) {
                    repeatedCount++;
                } else {
                    if (repeatedCount > 0) {
                        repeatedCharacters.add(value.slice(i - repeatedCount, i + 1));
                        repeatedCount = 0;
                    }
                }

                // Check for consecutive sequence (e.g., 'abc')
                if (nextChar === currentChar + 1) {
                    consecutiveCount++;
                } else {
                    if (consecutiveCount > 0) {
                        consecutiveSequences.add(value.slice(i - consecutiveCount, i + 1));
                        consecutiveCount = 0;
                    }
                }

                // Check for reverse consecutive sequence (e.g., 'cba')
                if (nextChar === currentChar - 1) {
                    reverseConsecutiveCount++;
                } else {
                    if (reverseConsecutiveCount > 0) {
                        reverseConsecutiveSequences.add(value.slice(i - reverseConsecutiveCount, i + 1));
                        reverseConsecutiveCount = 0;
                    }
                }
            }

            [
                [repeatedCharacters, repeatedCount, 5],
                [consecutiveSequences, consecutiveCount, 4],
                [reverseConsecutiveSequences, reverseConsecutiveCount, 3],
            ].forEach(([set, count, deduction]) => {
                if (count > 0) {
                    set.add(value.slice(value.length - count - 1));
                }

                if (set.size > 0) {
                    score -= deduction;
                    set.forEach((item) => (score -= Math.min(item.length, 10)));
                }
            });

            return Math.min(Math.max(score, 1), 100);
        },

        getInfoText(score) {
            if (score >= 90) {
                return __('s.1w');
            } else if (score >= 80) {
                return __('s.B');
            } else if (score >= 70) {
                return __('s.1C');
            } else if (score >= 50) {
                return __('s.x');
            } else if (score >= 30) {
                return __('s.1q');
            } else if (score >= 20) {
                return __('s.a');
            } else {
                return __('s.1g');
            }
        },
    };
}
